import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import BookmarkService from "../../../../services/bookmark.service";
import { getTotalTime } from "../../../../util/getTotalTime";
import Input from "../../../Form/Input";
import Modal from "../../../Generic/Modal";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import useStyles from "./styles";

type CommentButtonMenuProps = {
  isOpen: boolean;
  hours: number;
  minutes: number;
  seconds: number;
  onClose: () => void;
};

export const CommentButtonModal = ({
  isOpen = false,
  hours,
  minutes,
  seconds,
  onClose,
}: CommentButtonMenuProps) => {
  const { t: translate } = useTranslation();

  const { room, task, taskIsValid, participantsAllowed } = useVideoContext();

  const classes = useStyles();

  const [comment, setComment] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleClose = () => {
    setComment("");
    onClose();
  };

  const { mutate: createComment } = useMutation(
    ["video.call.video.survey.room.comment"],
    (comment: any) => BookmarkService.create(comment),
    {
      onSuccess: () => {
        setIsSaving(false);

        NotificationToast.success(translate("commentsSavedSuccessfully"));

        handleClose();
      },
      onError: () => {
        setIsSaving(false);

        NotificationToast.error(translate("couldNotSaveComments"));

        handleClose();
      },
    }
  );

  const onCreateComment = () => {
    if (taskIsValid()) {
      setIsSaving(true);

      createComment({
        videoTime: getTotalTime(hours, minutes, seconds),
        markDate: new Date(),
        comments: comment,
        taskId: task?.id,
      });
    }
  };

  return (
    <Modal
      title={translate("saveAComment")}
      className={classes.modal}
      open={isOpen}
      showActionButtons
      actionButtonProps={{
        textSave: translate("saveComment"),
        saveDisabled: !room || participantsAllowed?.length === 0 || isSaving,
        loading: isSaving,
        onCancel: () => handleClose(),
        onSave: () => onCreateComment(),
      }}
      handleClose={() => handleClose()}
    >
      <Input
        name="comments"
        placeholder={translate("yourComment")}
        value={comment}
        onChange={(e) => setComment(e?.target?.value)}
        type="text"
        minRows={4}
        multiline
        fullWidth
      />
    </Modal>
  );
};
